import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '@/layouts/AuthLayout';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { useAuth } from '@/providers/AuthProvider';
import { useFormErrors } from '@/hooks/useFormErrors';
import { safeReturnPath } from '@/utils/navigation';

/**
 * Sign in — BUILD-PLAN §17, §41 (loading/error states), §27 (mobile forms).
 */
export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fieldErrors, formError, handleError, reset } = useFormErrors();

  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    reset();
    setSubmitting(true);

    try {
      await login({ email, password, remember });

      navigate(safeReturnPath(searchParams.get('returnTo'), '/account'), { replace: true });
    } catch (error) {
      handleError(error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="Welcome back"
      description="Sign in to your Glanz Luxe account."
      footer={
        <>
          New to Glanz Luxe?{' '}
          <Link to="/register" className="text-accent underline underline-offset-4">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
        {formError && <Alert tone="error">{formError}</Alert>}

        <Input
          label="Email address"
          type="email"
          name="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={fieldErrors['email']}
          autoComplete="email"
          // Correct virtual keyboard and no auto-capitalising of addresses
          // on mobile (§27 keyboard-safe inputs).
          inputMode="email"
          autoCapitalize="none"
          spellCheck={false}
          required
        />

        <Input
          label="Password"
          type="password"
          name="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={fieldErrors['password']}
          autoComplete="current-password"
          required
        />

        <div className="flex items-center justify-between gap-4">
          <label className="flex items-center gap-2 text-sm text-muted">
            <input
              type="checkbox"
              checked={remember}
              onChange={(event) => setRemember(event.target.checked)}
              className="h-4 w-4 accent-accent"
            />
            Remember me
          </label>

          <Link
            to="/forgot-password"
            className="text-sm text-accent underline underline-offset-4"
          >
            Forgot password?
          </Link>
        </div>

        <Button type="submit" loading={submitting} fullWidth size="lg">
          Sign In
        </Button>
      </form>
    </AuthLayout>
  );
}
